/*
 * Message Editor detail — SANCTIONED Class B popup exception to BO UI Standard §4 /
 * standing rule 12 (which mandates the full-page SettingsDetailPage). This detail
 * carries a nested "Translations" child-collection grid (Language + Translation,
 * with its own add/edit/delete) that the generic FLAT SettingsDetailPage cannot
 * express — the same category as MsrDetailDialog. It still honours the save-flow
 * essence: confirm → audited useSaveFlow → shared "Saved successfully." (useNotify).
 * Do NOT flatten into SettingsDetailPage.
 */
import { useEffect, useLayoutEffect, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useConfirm, useSaveFlow } from '../../../components/confirm';
import { api } from '../../../api/client';
import {
  useMessageEditor, useMessageModules, useMessageBos, useSaveMessageEditor,
  useMessageTranslations, useSaveMessageTranslation, useDeleteMessageTranslation,
  ACTION_TYPES, GLOSSARY_LANGUAGES, errMessage, type GlossaryTranslation,
} from './systemMasters';

interface Props { id: string | null; onClose: () => void; cardRef?: React.RefObject<HTMLDivElement | null>; }

interface FormState { message: string; messageKey: string; moduleId: string; boId: string; actionType: number; remark: string; }
const BLANK: FormState = { message: '', messageKey: '', moduleId: '', boId: '', actionType: 0, remark: '' };
const langName = (v: number) => GLOSSARY_LANGUAGES.find((l) => l.v === v)?.t ?? String(v);

/**
 * Message Editor detail form — LIMS MessageEditor_DetailView. MS ID auto-generated; Message / Module / BO
 * required (Module/BO are cascading DefaultSetting dropdowns); Action Type = radio group; Remark free text;
 * a Translations child grid (staged on a new message, committed on Save). Save / Save and Close / Save and New / Cancel.
 */
export function MessageEditorDetailDialog({ id, onClose, cardRef }: Props) {
  const notify = useNotify();
  const { confirm, alert } = useConfirm();
  const runSave = useSaveFlow();
  const save = useSaveMessageEditor();
  const [msgId, setMsgId] = useState<string | null>(id);   // current message id (set after a new one is created)
  const isNew = !msgId;

  // Opens maximized filling the grid card (same as the Glossary / State detail forms).
  const [maxed, setMaxed] = useState(true);
  const [maxRect, setMaxRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!maxed) { setMaxRect(null); return; }
    const measure = () => { const r = cardRef?.current?.getBoundingClientRect(); if (r) setMaxRect({ top: r.top, left: r.left, width: r.width, height: r.height }); };
    measure(); window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [maxed, cardRef]);
  const maxStyle = maxed && maxRect
    ? { position: 'fixed' as const, top: maxRect.top, left: maxRect.left, width: maxRect.width, height: maxRect.height, maxWidth: 'none' as const, maxHeight: 'none' as const, margin: 0 }
    : undefined;

  const [form, setForm] = useState<FormState>(BLANK);
  const [err, setErr] = useState('');
  const detail = useMessageEditor(msgId ?? undefined);
  useEffect(() => {
    if (detail.data) setForm({
      message: detail.data.message ?? '', messageKey: detail.data.messageKey ?? '',
      moduleId: detail.data.moduleId ?? '', boId: detail.data.boId ?? '',
      actionType: detail.data.actionType, remark: detail.data.remark ?? '',
    });
  }, [detail.data]);

  const { data: modules = [] } = useMessageModules();
  const { data: bos = [] } = useMessageBos(form.moduleId || undefined);
  const set = (patch: Partial<FormState>) => { setErr(''); setForm((f) => ({ ...f, ...patch })); };

  // ── Translations child (staged on a new message; committed on Save) ──
  const { data: translations = [] } = useMessageTranslations(msgId ?? undefined);
  const saveTr = useSaveMessageTranslation(msgId ?? '');
  const delTr = useDeleteMessageTranslation(msgId ?? '');
  const [trSel, setTrSel] = useState<Set<string | number>>(new Set());
  const [trEdit, setTrEdit] = useState<{ id?: string; language: number; translation: string } | null>(null);
  const [trErr, setTrErr] = useState('');
  const [staged, setStaged] = useState<GlossaryTranslation[]>([]);
  const isStaged = (tid: string | number) => String(tid).startsWith('tmp-');
  const staging = !msgId;
  const trRows = [...translations, ...staged];
  const selStaged = trSel.size === 1 && isStaged([...trSel][0]!);

  const doSave = (mode: 'stay' | 'close' | 'new') => {
    // LIMS parity: Module/BO are read-only (auto-assigned) AND required (RuleRequiredField) — so neither a
    // new message nor a message whose Module/BO are blank can be saved. Show the verbatim LIMS message.
    if (!form.moduleId || !form.boId) {
      setErr('"Module" and "BO" must not be empty.');
      return;
    }
    setErr('');
    const wasNew = !msgId;
    let newId = msgId;
    void runSave({
      save: async () => {
        const res = await save.mutateAsync({
          id: msgId ?? undefined, message: form.message.trim(), messageKey: form.messageKey.trim() || null,
          moduleId: form.moduleId, boId: form.boId, actionType: form.actionType, remark: form.remark.trim() || null,
        });
        newId = msgId ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id ?? null;
        if (wasNew && newId && staged.length) {
          for (const tr of staged) await api.post(`/api/v1/system/message-editor/${newId}/translations`, { language: tr.language, translation: tr.translation });
        }
      },
      successMessage: 'Saved successfully.',
      onSaved: () => {
        setStaged([]); setTrSel(new Set());
        if (mode === 'close') { onClose(); return; }
        if (mode === 'new') { setMsgId(null); setForm(BLANK); return; }   // Save and New → blank form
        if (wasNew && newId) setMsgId(newId);   // Save (stay) → switch to edit; translations now committed
      },
      onError: (e: unknown) => setErr(errMessage(e)),
    });
  };

  const openTrEdit = (v: { id?: string; language: number; translation: string }) => { setTrErr(''); setTrEdit(v); };
  const applyTr = async () => {
    if (!trEdit) return;
    const text = trEdit.translation.trim();
    if (!text) { setTrErr('Translation must not be empty.'); return; }
    if (trRows.some((t) => t.id !== trEdit.id && t.language === trEdit.language && (t.translation ?? '').trim() === text)) { setTrErr('This translation already exists.'); return; }
    setTrErr('');
    if (!trEdit.id) {
      if (staging) {   // new message → stage in memory, committed on Save
        const tmp: GlossaryTranslation = { id: `tmp-${crypto.randomUUID()}`, language: trEdit.language, translation: text };
        setStaged((s) => [...s, tmp]); setTrSel(new Set([tmp.id])); setTrEdit(null); return;
      }
      try { const res = await saveTr.mutateAsync({ language: trEdit.language, translation: text }); const nid = (res as { data?: { data?: { id?: string } } })?.data?.data?.id; if (nid) setTrSel(new Set([nid])); setTrEdit(null); }
      catch (e) { setTrErr(errMessage(e)); }
      return;
    }
    try { await saveTr.mutateAsync({ id: trEdit.id, language: trEdit.language, translation: text }); setTrSel(new Set([trEdit.id])); setTrEdit(null); }
    catch (e) { setTrErr(errMessage(e)); }
  };
  const delSelectedTr = async () => {
    if (trSel.size === 0) { notify.warning('Select a translation to delete.'); return; }
    if (!await confirm({ title: 'Delete', tone: 'danger', confirmLabel: 'Delete', message: `Delete ${trSel.size} translation${trSel.size === 1 ? '' : 's'}?` })) return;
    const ids = [...trSel].map(String);
    try {
      const stagedIds = ids.filter(isStaged);
      if (stagedIds.length) setStaged((s) => s.filter((t) => !stagedIds.includes(t.id)));
      for (const tid of ids.filter((i) => !isStaged(i))) await delTr.mutateAsync(tid);
      setTrSel(new Set());
      await alert({ title: 'Success', message: 'Deleted successfully.', tone: 'success' });
    } catch (e) { notify.error(errMessage(e)); }
  };
  const editSelectedTr = () => {
    const tid = [...trSel][0]; if (!tid || isStaged(tid)) return;
    const t = trRows.find((x) => x.id === tid); if (t) openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' });
  };

  const trCols: AppColumn<GlossaryTranslation>[] = [
    { field: 'edit', header: '', sortable: false, filterable: false, width: 40, align: 'center',
      render: (t) => isStaged(t.id)
        ? <button className="pts-icon-btn edit" disabled title="Save the message to edit this translation"><i className="ti ti-edit" /></button>
        : <button className="pts-icon-btn edit" title="Edit" onClick={(e) => { e.stopPropagation(); openTrEdit({ id: t.id, language: t.language, translation: t.translation ?? '' }); }}><i className="ti ti-edit" /></button> },
    { field: 'language', header: 'Language', width: '40%', minWidth: 140, sortable: false, text: (r) => langName(r.language) },
    { field: 'translation', header: 'Translation', width: '60%', minWidth: 200, sortable: false, text: (r) => r.translation ?? '' },
  ];

  return (
    <div className="acc-overlay" onMouseDown={onClose}>
      <div className={`acc-dialog acc-dialog-lg gloss-detail${maxed ? ' acc-dialog-max' : ''}`} style={maxStyle} onMouseDown={(e) => e.stopPropagation()}>
        <div className="acc-dialog-hdr">
          <span>Message Editor{!isNew && detail.data?.msid ? ` - ${detail.data.msid}` : ''}</span>
          <span className="acc-dialog-acts">
            <i className={`ti ${maxed ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={maxed ? 'Restore' : 'Maximize'} onClick={() => setMaxed((m) => !m)} />
            <i className="ti ti-x acc-dialog-x" onClick={onClose} />
          </span>
        </div>
        <div className="acc-dialog-body">
          {err && <div className="acc-err" style={{ margin: '0 0 8px' }}>{err}</div>}
          <div className="gloss-form me-form">
            <Field label="MS ID"><input className="acc-input gloss-ro" value={isNew ? '' : (detail.data?.msid ?? '')} placeholder="Autogenerate" readOnly /></Field>
            <Field label="Message"><textarea className="acc-input gloss-def" rows={2} value={form.message} onChange={(e) => set({ message: e.target.value })} /></Field>
            {/* MessageKey / Module / BO are read-only in LIMS (AllowEdit="False") — auto-assigned, not user-set. */}
            <Field label="Message Key"><input className="acc-input gloss-ro" value={form.messageKey} readOnly /></Field>
            <Field label="Module" req>
              <select className="acc-input gloss-ro" value={form.moduleId} disabled>
                <option value="">N/A</option>
                {modules.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
            <Field label="BO" req>
              <select className="acc-input gloss-ro" value={form.boId} disabled>
                <option value="">N/A</option>
                {bos.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Action Type" tall>
              <div className="gloss-radio-group">
                {ACTION_TYPES.map((a) => (
                  <label key={a.v} className="gloss-radio">
                    <input type="radio" name="me-action" checked={form.actionType === a.v} onChange={() => set({ actionType: a.v })} />
                    {a.t}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Remark"><textarea className="acc-input gloss-def" rows={2} value={form.remark} onChange={(e) => set({ remark: e.target.value })} /></Field>
          </div>

          <div className="gloss-tr">
            <div className="acc-sub-title">Translations</div>
            <div className="acc-sub-toolbar">
              <button className="ca-btn" onClick={() => openTrEdit({ language: 0, translation: '' })}><i className="ti ti-plus" /> New</button>
              <button className="ca-btn" onClick={editSelectedTr} disabled={trSel.size !== 1 || selStaged}><i className="ti ti-edit" /> Edit</button>
              <button className="ca-btn" onClick={() => void delSelectedTr()} disabled={trSel.size === 0}><i className="ti ti-trash" /> Delete</button>
              <ExportMenu triggerClassName="ca-btn" filename="MessageEditor-Translations" headers={['Language', 'Translation']}
                rows={trRows.map((t) => [langName(t.language), t.translation ?? ''])} />
            </div>
            <AppDataGrid<GlossaryTranslation> rows={trRows} columns={trCols} getRowId={(r) => r.id}
              selectable selectedIds={trSel} onSelectionChange={setTrSel}
              emptyText="No data to display." defaultPageSize={20} />
          </div>
        </div>
        <div className="acc-dialog-ftr">
          <button className="ca-btn ca-btn-solid" onClick={() => doSave('stay')} disabled={save.isPending}><span className="tb-i">💾</span> Save</button>
          <button className="ca-btn" onClick={() => doSave('close')} disabled={save.isPending}><span className="tb-i">💾</span> Save and Close</button>
          <button className="ca-btn" onClick={() => doSave('new')} disabled={save.isPending}><span className="tb-i">💾</span> Save and New</button>
          <button className="ca-btn" onClick={onClose} disabled={save.isPending}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
        </div>
      </div>

      {trEdit && (
        <div className="acc-overlay" onMouseDown={() => setTrEdit(null)}>
          <div className="acc-dialog gloss-tr-edit" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr"><span>{trEdit.id ? 'Edit Translation' : 'New Translation'}</span><i className="ti ti-x acc-dialog-x" onClick={() => setTrEdit(null)} /></div>
            <div className="acc-dialog-body">
              <Field label="Language" req tall>
                <div className="gloss-radio-group">
                  {GLOSSARY_LANGUAGES.map((l) => (
                    <label key={l.v} className="gloss-radio">
                      <input type="radio" name="me-tr-lang" checked={trEdit.language === l.v} onChange={() => { setTrErr(''); setTrEdit({ ...trEdit, language: l.v }); }} />
                      {l.t}
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Translation" req><input className="acc-input" value={trEdit.translation} onChange={(e) => { setTrErr(''); setTrEdit({ ...trEdit, translation: e.target.value }); }} /></Field>
              {trErr && <div className="gloss-tr-err">{trErr}</div>}
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void applyTr()}><span className="tb-i">💾</span> OK</button>
              <button className="ca-btn" onClick={() => setTrEdit(null)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, req, tall, children }: { label: string; req?: boolean; tall?: boolean; children: React.ReactNode }) {
  return (
    <div className={`acc-field-h${tall ? ' gloss-field-tall' : ''}`}>
      <label className={`acc-label${req ? ' gloss-req' : ''}`}>{label}{req && <span className="gloss-star">*</span>}</label>
      <div className="acc-fh-control">{children}</div>
    </div>
  );
}
